<template>
  <div class="isolate overflow-hidden bg-black">
    <div class="mx-auto max-w-7xl px-6 pb-96 pt-24 text-center sm:pt-32 lg:px-8">
      <div class="mx-auto">
        <p v-if="!isRegistrationClosed" class="font-roboto my-2 text-3xl sm:text-5xl font-bold tracking-tight text-white">
          Join the next Production-Ready Serverless cohort.
        </p>
        <p v-else class="font-roboto my-2 text-3xl sm:text-5xl font-bold tracking-tight text-white">
          Registration for this cohort is closed.
        </p>
        <p v-if="!isRegistrationClosed" class="mx-auto max-w-3xl text-xl leading-8 text-white/90">
          Get 4 weeks of structured training, hands-on exercises, live Q&A, implementation tracks
          for your stack, lifetime access, and direct support from Yan.
        </p>
        <p v-else class="mx-auto max-w-3xl text-xl leading-8 text-white/90">
          Join the waitlist to be the first to know when the next Production-Ready Serverless cohort opens.
        </p>
      </div>
      <div class="relative mt-6">
        <template v-if="!isRegistrationClosed">
          <p class="mx-auto text-lg leading-8 text-white/60">
            The next cohort starts on <span class="text-white font-medium">{{ Constants.START_DATE }}</span>.
          </p>

          <p v-if="isEarlyBird" class="text-white/60 mt-6">
            <span class="text-white">EARLY BIRD</span> prices (
              <span class="font-bold text-yellow-200 text-xl mx-2">30% OFF</span>
            ) are available until
            <span class="text-white">{{ Constants.EARLY_BIRD_DATE }}</span>
          </p>
          <p v-if="pppDiscount" class="text-white/60 mt-6">
            Purchase Power Parity (PPP) discount (
              <span class="font-bold text-yellow-200 text-xl mx-2">{{ pppDiscount }}% OFF</span>
            ) will be automatically applied on your order during checkout.
          </p>
          <p v-if="holidayDiscount" class="text-white/60 mt-6">
            <span class="text-white">{{ holidayName }} Sale</span> discount (
              <span class="font-bold text-yellow-200 text-xl mx-2">{{ holidayDiscount }}% OFF</span>
            ) will be automatically applied on your order during checkout.
          </p>
          <p v-if="(pppDiscount || 0) < newsletterDiscount && (holidayDiscount || 0) < newsletterDiscount" class="text-white/60 mt-6">
            Get <span class="font-bold text-yellow-200 text-xl mx-2">{{newsletterDiscount}}% OFF</span>
            by <a href="#subscribe" class="text-white underline underline-offset-2">joining our newsletter</a>.
          </p>
        </template>

        <svg viewBox="0 0 1208 1024" class="absolute -top-10 left-1/2 -z-10 h-[64rem] -translate-x-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] sm:-top-12 md:-top-20 lg:-top-12 xl:top-0">
          <ellipse cx="604" cy="512" fill="url(#6d1bd035-0dd1-437e-93fa-59d316231eb0)" rx="604" ry="512" />
          <defs>
            <radialGradient id="6d1bd035-0dd1-437e-93fa-59d316231eb0">
              <stop stop-color="#7775D6" />
              <stop offset="1" stop-color="#E935C1" />
            </radialGradient>
          </defs>
        </svg>
      </div>
    </div>
    <div class="flow-root bg-white pb-24 sm:pb-32">
      <div class="-mt-80">
        <div v-if="!isRegistrationClosed" class="mx-auto max-w-7xl px-6 lg:px-8">
          <div class="mx-auto grid max-w-md grid-cols-1 gap-8 lg:max-w-4xl lg:grid-cols-2">
            <div v-for="plan in [singlePaymentPlan, instalmentPlan]" :key="plan.id" class="flex flex-col justify-between rounded-3xl bg-white p-8 shadow-xl ring-1 ring-gray-900/10 sm:p-10">
              <div>
                <h3 :id="plan.id" class="text-base font-semibold leading-7 text-indigo-600">{{ plan.name }}</h3>
                <div class="mt-4 flex items-baseline gap-x-2">
                  <span v-if="plan.price < plan.priceFull">
                    <span class="text-2xl font-bold tracking-tight text-gray-500 line-through mr-2">${{ plan.priceFull }}</span>
                    <span class="text-5xl font-bold tracking-tight text-gray-900">${{ plan.price }}</span>
                  </span>
                  <span v-if="plan.price >= plan.priceFull" class="text-5xl font-bold tracking-tight text-gray-900">${{ plan.priceFull }}</span>
                  <span v-if="plan.frequency" class="text-3xl font-semibold leading-7 text-gray-600">{{ plan.frequency }}</span>
                </div>
                <p class="mt-6 text-base sm:text-lg leading-7 text-gray-600">{{ plan.description }}</p>
                <ul role="list" class="mt-10 space-y-4 text-sm sm:text-base leading-6">
                  <li v-for="feature in plan.features" :key="feature" class="flex gap-x-3">
                    <svg class="h-6 w-5 flex-none text-green-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                      <path fillRule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd" />
                    </svg>

                    {{ feature }}
                  </li>
                </ul>
              </div>
              <button @click="handleClick(plan.id)" target="_blank" :aria-describedby="plan.id" class="mt-8 block rounded-md bg-indigo-600 hover:bg-indigo-500 px-3.5 py-2 text-center text-xl font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Join the cohort
              </button>
            </div>
          </div>

          <div class="mx-auto mt-8 max-w-md lg:max-w-4xl">
            <div class="rounded-3xl bg-white p-8 shadow-xl ring-1 ring-gray-900/10 sm:p-10 text-left">
              <h3 id="team-pricing" class="text-base font-semibold leading-7 text-indigo-600">Team pricing</h3>
              <p class="mt-2 text-base sm:text-lg leading-7 text-gray-600">
                Buying for a team? Save more the larger your group.
              </p>

              <div class="mt-8 flex flex-col items-center rounded-2xl bg-gray-50 p-6 ring-1 ring-gray-200">
                <p class="text-sm font-semibold uppercase tracking-wide text-gray-500">Team size</p>
                <div class="mt-3 flex items-center gap-x-4">
                  <button @click="decrementSeats" :disabled="atMin" aria-label="Decrease team size" class="flex h-10 w-10 items-center justify-center rounded-full bg-white ring-1 ring-gray-300 text-2xl font-bold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">−</button>
                  <span class="min-w-[4rem] text-center text-4xl font-bold tracking-tight text-gray-900">{{ seatsLabel }}</span>
                  <button @click="incrementSeats" :disabled="atContactMax" aria-label="Increase team size" class="flex h-10 w-10 items-center justify-center rounded-full bg-white ring-1 ring-gray-300 text-2xl font-bold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">+</button>
                </div>
                <p class="mt-3 text-xs text-gray-500">Choose from {{ minSeats }} to {{ maxSeats }}+ seats</p>
              </div>

              <div v-if="!isContactMode" class="mt-6">
                <div class="flex items-center justify-between gap-x-2">
                  <p class="text-sm font-semibold uppercase tracking-wide text-gray-500">Team total</p>
                  <span class="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                    Total savings: {{ teamSavingsPct }}%
                  </span>
                </div>
                <div class="mt-3 flex items-baseline gap-x-2">
                  <span class="text-xl font-bold tracking-tight text-gray-500 line-through">${{ teamPriceFull }}</span>
                  <span class="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">${{ teamPrice }}</span>
                </div>
                <p class="mt-1 text-sm text-gray-500">${{ teamPerSeat }} per seat (including {{ Math.round(selectedTier.discount * 100) }}% team discount)</p>
                <button @click="handleTeamClick" aria-describedby="team-pricing" class="mt-6 w-full block rounded-md bg-indigo-600 hover:bg-indigo-500 px-3.5 py-2 text-center text-xl font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  Buy for team
                </button>
              </div>

              <div v-else class="mt-6">
                <p class="text-base sm:text-lg leading-7 text-gray-600">
                  Need more than {{ maxSeats }} seats? Get in touch for a custom quote.
                </p>
                <a target="_blank" :href="contactHref" @click="handleContactClick" aria-describedby="team-pricing" class="mt-6 w-full block rounded-md bg-indigo-600 hover:bg-indigo-500 px-3.5 py-2 text-center text-xl font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  Contact me
                </a>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="mx-auto max-w-md lg:max-w-2xl px-6 lg:px-8">
          <div class="rounded-3xl bg-white p-8 shadow-xl ring-1 ring-gray-900/10 sm:p-10 text-left">
            <h3 id="waitlist" class="text-base font-semibold leading-7 text-indigo-600">Join the waitlist</h3>
            <p class="mt-2 text-base sm:text-lg leading-7 text-gray-600">
              Get notified the moment the next cohort opens, plus early-bird discounts.
            </p>
            <div id="waitlist-form-placeholder" class="mt-6"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCookie } from '@/libs/utils'
import { fathomEvent } from '@/libs/analytics'
import * as Constants from '@/libs/constants'

export default {
  data () {
    const sourceUrl = window.location.origin
    const userAgent = navigator.userAgent

    let urlSuffix = `?sourceUrl=${sourceUrl}&userAgent=${userAgent}`

    const singlePaymentUrl = `https://theburningmonk.gumroad.com/l/${Constants.SINGLE_PAYMENT_ID}`
    const installmentUrl = `https://theburningmonk.gumroad.com/l/${Constants.INSTALLMENT_ID}`

    const affiliateId = getCookie('affiliateId')
    if (affiliateId) {
      urlSuffix += `&affiliateId=${affiliateId}`
    }

    const now = new Date()
    const isEarlyBird = now < Constants.EARLY_BIRD_DEADLINE
    const isRegistrationClosed = now >= Constants.CLOSE_DEADLINE
    const singlePaymentPrice = isEarlyBird ? Constants.PRICE : Constants.PRICE_FULL
    const instalmentPrice = isEarlyBird ? Constants.INSTALLMENT_PRICE : Constants.INSTALLMENT_PRICE_FULL
    const instalmentTotal = instalmentPrice * Constants.INSTALLMENTS
    const delta = instalmentTotal - singlePaymentPrice

    const singlePaymentPlan = {
      name: 'Single payment',
      id: 'single-payment',
      href: singlePaymentUrl,
      price: singlePaymentPrice,
      priceFull: Constants.PRICE_FULL,
      description: `Save $${delta} with single payment.`,
      features: [
        'All four tracks - Serverless Framework, CDK, AWS SAM and Terraform',
        'Live Q&A sessions',
        'Lifetime access to all content',
        'Lifetime access to private community',
        'Happiness guarantee',
        'Team discount available'
      ]
    }

    const instalmentPlan = {
      name: 'Installment plan',
      id: 'installment-plan',
      href: installmentUrl,
      price: instalmentPrice,
      priceFull: Constants.INSTALLMENT_PRICE_FULL,
      frequency: ` x ${Constants.INSTALLMENTS}`,
      description: `Pay in ${Constants.INSTALLMENTS} easy installments.`,
      features: [
        'All four tracks - Serverless Framework, CDK, AWS SAM and Terraform',
        'Live Q&A sessions',
        'Lifetime access to all content',
        'Lifetime access to private community',
        'Happiness guarantee',
        'Pay in 5 installments instead of a lump sum'
      ]
    }

    return {
      isEarlyBird,
      isRegistrationClosed,
      singlePaymentPlan,
      instalmentPlan,
      plans: [singlePaymentPlan, instalmentPlan],
      urlSuffix,
      Constants,
      pppDiscount: 0,
      holidayName: null,
      holidayDiscount: 0,
      newsletterDiscount: Constants.NEWSLETTER_DISCOUNT,
      selectedSeats: Constants.TEAM_PLANS[0].seats,
      isContactMode: false
    }
  },
  mounted () {
    if (this.isRegistrationClosed) {
      const script = document.createElement('script')
      script.async = true
      script.dataset.uid = Constants.WAITLIST_FORM_UID
      script.src = `https://theburningmonk.ck.page/${Constants.WAITLIST_FORM_UID}/index.js`
      const placeholder = document.getElementById('waitlist-form-placeholder')
      if (placeholder) {
        placeholder.appendChild(script)
      }
    }
  },
  computed: {
    teamSeatSizes () {
      return Constants.TEAM_PLANS.map(t => t.seats)
    },
    minSeats () {
      return this.teamSeatSizes[0]
    },
    maxSeats () {
      return this.teamSeatSizes[this.teamSeatSizes.length - 1]
    },
    atMin () {
      return !this.isContactMode && this.selectedSeats <= this.minSeats
    },
    atContactMax () {
      return this.isContactMode
    },
    seatsLabel () {
      return this.isContactMode ? `${this.maxSeats}+` : this.selectedSeats
    },
    selectedTier () {
      return Constants.TEAM_PLANS
        .filter(t => t.seats <= this.selectedSeats)
        .pop() || Constants.TEAM_PLANS[0]
    },
    teamPriceFull () {
      return Constants.PRICE_FULL * this.selectedSeats
    },
    teamPrice () {
      return Math.round(this.singlePaymentPlan.price * this.selectedSeats * (1 - this.selectedTier.discount))
    },
    teamPerSeat () {
      return Math.round(this.teamPrice / this.selectedSeats)
    },
    teamSavingsPct () {
      return Math.round((1 - this.teamPrice / this.teamPriceFull) * 100)
    },
    contactHref () {
      return 'mailto:theburningmonk@hey.com?subject=Group%20discount%20for%20Production-Ready%20Serverless'
    }
  },
  methods: {
    handleClick (id) {
      fathomEvent(`clicked-${id}`)

      const plan = this.plans.find(p => p.id === id)
      const url = plan.href + this.urlSuffix
      window.open(url, '_blank')
    },

    incrementSeats () {
      if (this.isContactMode) return
      if (this.selectedSeats >= this.maxSeats) {
        this.isContactMode = true
        return
      }
      const next = this.teamSeatSizes.find(s => s > this.selectedSeats)
      if (next !== undefined) this.selectedSeats = next
    },

    decrementSeats () {
      if (this.isContactMode) {
        this.isContactMode = false
        return
      }
      const prev = [...this.teamSeatSizes].reverse().find(s => s < this.selectedSeats)
      if (prev !== undefined) this.selectedSeats = prev
    },

    handleTeamClick () {
      const tier = this.selectedTier
      fathomEvent(`clicked-team-${this.selectedSeats}`)
      const url = `https://theburningmonk.gumroad.com/l/${Constants.SINGLE_PAYMENT_ID}` + this.urlSuffix + `&option=${tier.optionId}`
      window.open(url, '_blank')
    },

    handleContactClick () {
      fathomEvent('clicked-team-contact')
    },

    handlePppDiscount (amount) {
      this.pppDiscount = amount

      this.applyDiscount(amount)
    },

    handleHolidayDiscount (name, amount, couponCode) {
      this.holidayName = name
      this.holidayDiscount = amount

      this.urlSuffix = `/${couponCode}` + this.urlSuffix

      this.applyDiscount(amount)
    },

    applyDiscount (amount) {
      const discountedRatio = 1 - amount / 100.0

      this.instalmentPlan.price = Math.round(this.instalmentPlan.price * discountedRatio)
      const instalmentTotal = this.instalmentPlan.price * Constants.INSTALLMENTS
      this.instalmentPlan.description = `Pay in ${Constants.INSTALLMENTS} easy installments.`

      this.singlePaymentPlan.price = Math.round(this.singlePaymentPlan.price * discountedRatio)
      const delta = instalmentTotal - this.singlePaymentPlan.price
      this.singlePaymentPlan.description = `Save $${delta} with single payment.`
    }
  }
}
</script>
